import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  ButtonBase,
  Skeleton,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  keyframes
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
// Removed unused LocalShippingIcon import
import whiteLogo from '../assets/Whitelogo09122025.png';

// Define the pulse animation from your CSS
const pulseAnimation = keyframes`
  0% { opacity: 0.9; transform: scale(1); }
  100% { opacity: 1; transform: scale(1.05); }
`;

// Removed unused floatAnimation

export default function HomepageTopAd({ adSpaceList }: { adSpaceList: any[] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 1. Filter for relevant ads
  const homepageAds = adSpaceList.filter(ad => ad.adPositionCode === 'homepage-top');

  // 2. State for Image Rotation
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  // 3. Effect for Rotation
  useEffect(() => {
    if (homepageAds.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % homepageAds.length);
        setImageLoaded(false);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [homepageAds]);

  // =========================================================
  // SCENARIO A: NO ADS FOUND -> RENDER "WORKHORSE BRAND CARD"
  // =========================================================
  if (homepageAds.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          minHeight: { xs: '320px', md: '390px' },
          borderRadius: '24px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          // THEME: Exact gradient from your HTML
          background: 'linear-gradient(135deg, #FFC72C 0%, #f5b640 35%, #1847b0 100%)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
        }}
      >
        {/* 1. Animated Background Overlay (Pulse Effect) */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.2), transparent 60%),
            radial-gradient(circle at 70% 80%, rgba(24, 71, 176, 0.25), transparent 70%)
          `,
          zIndex: 0,
          animation: `${pulseAnimation} 6s ease-in-out infinite alternate`
        }} />

        {/* 2. Content Container */}
        <Box sx={{
          position: 'relative',
          zIndex: 2,
          px: { xs: 3, md: 8 },
          py: { xs: 4, md: 0 },
          maxWidth: { xs: '100%', md: '70%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}>

          {/* Headline - Matching the landing page style */}
          <Typography variant={isMobile ? "h4" : "h3"} sx={{
            fontWeight: 800,
            color: '#fff', // White text on the gradient
            lineHeight: 1.1,
            mb: 2,
            fontFamily: '"Inter", sans-serif',
            textShadow: '0px 2px 10px rgba(0,0,0,0.1)'
          }}>
            Singapore’s Specialized <br />
            <Box component="span" sx={{ color: '#FFFACD' }}> {/* Light Yellow Highlight */}
              Commercial Platform
            </Box>
          </Typography>

          {/* Body Text */}
          <Typography variant="body1" sx={{
            color: 'rgba(255,255,255,0.9)',
            mb: 4,
            maxWidth: '500px',
            lineHeight: 1.6,
            fontWeight: 500
          }}>
            Browse listings, compare options & connect with trusted dealers.
            Built for Dealers, Businesses & Professional Buyers.
          </Typography>

          {/* CTA Button - White/Blue to pop against the Orange/Blue gradient */}
          <Button
            component="a"
            href="/used-vehicles"
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: '50px',
              minWidth: { xs: '80px', md: '120px' },
              height: { xs: '40px', md: '45px' },
              background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
              color: '#fff',
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 700,
              boxShadow: 'none',
              ml: 1,
              '&:hover': { background: 'linear-gradient(135deg, #FF8E00 0%, #FFA100 100%)', color: '#030303ff' },
            }}
          >
            Browse Listings
          </Button>
        </Box>

        {/* REMOVED: 3. Visual Decoration (Right Side - Floating Truck/Logo) */}
      </Paper>
    );
  }

  // =========================================================
  // SCENARIO B: ADS FOUND -> RENDER IMAGE AD
  // =========================================================
  const currentAd = homepageAds[currentIndex];

  return (
    <ButtonBase
      component="a"
      href={currentAd.adSpaceThumbnailUrl || '#'}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        width: '100%',
        display: 'block',
        textAlign: 'left',
        // Keeps the 2:1 ratio for uploaded ads
        aspectRatio: '1600 / 800',
        borderRadius: '24px',
        '&:focus-visible': { outline: '2px solid #FFA100' }
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: '24px',
          backgroundColor: '#fff',
          boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
          border: '1px solid #f0f0f0',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {!imageLoaded && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            sx={{ position: 'absolute', top: 0, left: 0 }}
          />
        )}

        <Box
          component="img"
          src={currentAd.adSpaceImageUrl}
          alt={currentAd.name || 'Advertisement'}
          onLoad={() => setImageLoaded(true)}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out',
          }}
        />
      </Paper>
    </ButtonBase>
  );
}